'use client';

import Image from 'next/image';
import { useCallback, useDeferredValue, useEffect, useId, useMemo, useState } from 'react';
import { ArrowRight, ArrowUpRight, BookOpen, Check, CircleAlert, Search, Sparkles, X } from 'lucide-react';
import {
  Box,
  ButtonBase,
  Chip,
  ClickAwayListener,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { AppLink } from '@/components/mui/app-link';
import { AppLinkButton } from '@/components/mui/app-link-button';
import { MarketingEyebrow, MarketingFrame } from '../_components/marketing-page';
import { ToolSectionNavigation } from '../ferramentas/_components/ToolSectionNavigation';
import {
  allReferenceTerms,
  glossaryEntries,
  referenceCategories,
  referenceSectionItems,
  referenceSections,
  type ReferenceAccent,
  type ReferenceCategory,
  type ReferenceSection,
  type ReferenceTerm,
  type ReferenceTermAvailability,
  type ReferenceVisual,
  type ReferenceVisualItem,
  type ReferenceVisualTone,
} from './reference-content';

const accentStyles: Record<ReferenceAccent, { color: string; soft: string; border: string }> = {
  primary: {
    color: 'hsl(var(--primary))',
    soft: 'hsl(var(--primary) / .1)',
    border: 'hsl(var(--primary) / .3)',
  },
  lilac: {
    color: 'hsl(276 42% 50%)',
    soft: 'hsl(276 42% 50% / .1)',
    border: 'hsl(276 42% 50% / .3)',
  },
  sage: {
    color: 'hsl(151 35% 36%)',
    soft: 'hsl(151 35% 36% / .1)',
    border: 'hsl(151 35% 36% / .3)',
  },
  ochre: {
    color: 'hsl(31 67% 42%)',
    soft: 'hsl(31 67% 42% / .1)',
    border: 'hsl(31 67% 42% / .3)',
  },
  amber: {
    color: 'hsl(42 67% 40%)',
    soft: 'hsl(42 67% 40% / .1)',
    border: 'hsl(42 67% 40% / .3)',
  },
};

const availabilityLabels: Record<ReferenceTermAvailability, string> = {
  platform: 'Indicador do produto',
  conditional: 'Depende da cobertura',
  educational: 'Conceito',
};

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const scrollBehavior = () => (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');

type SearchResult = {
  id: string;
  title: string;
  sectionId: string;
  sectionTitle: string;
  summary: string;
};

const getSearchResults = (query: string): SearchResult[] => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  return allReferenceTerms
    .filter((term) => {
      const haystack = normalizeSearchText(
        [term.title, term.summary, ...(term.aliases ?? []), term.sectionTitle].join(' '),
      );
      return haystack.includes(normalizedQuery);
    })
    .slice(0, 8)
    .map(({ id, title, sectionId, sectionTitle, summary }) => ({
      id,
      title,
      sectionId,
      sectionTitle,
      summary,
    }));
};

const getSectionTerm = (section: ReferenceSection, selectedId?: string) =>
  section.terms.find((term) => term.id === selectedId) ?? section.terms[0];

const getTermAccent = (section: Pick<ReferenceSection, 'accent'>) => accentStyles[section.accent];

function SectionIcon({ section }: { section: ReferenceSection }) {
  const Icon = section.icon;
  const accent = getTermAccent(section);

  return (
    <Box
      sx={{
        display: 'grid',
        placeItems: 'center',
        width: { xs: 38, md: 42 },
        height: { xs: 38, md: 42 },
        flexShrink: 0,
        border: `1px solid ${accent.border}`,
        borderRadius: 1.5,
        color: accent.color,
        backgroundColor: accent.soft,
      }}
    >
      <Icon size={21} strokeWidth={1.8} aria-hidden='true' />
    </Box>
  );
}

function SearchField({
  value,
  onChange,
  onFocus,
  onKeyDown,
  onClear,
  resultsId,
  activeResultId,
  expanded,
}: {
  value: string;
  onChange: (value: string) => void;
  onFocus: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onClear: () => void;
  resultsId: string;
  activeResultId?: string;
  expanded: boolean;
}) {
  return (
    <TextField
      fullWidth
      id='reference-search'
      name='reference-search'
      autoComplete='off'
      type='search'
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      inputProps={{
        role: 'combobox',
        'aria-autocomplete': 'list',
        'aria-controls': resultsId,
        'aria-expanded': expanded,
        'aria-haspopup': 'listbox',
        'aria-activedescendant': activeResultId,
      }}
      placeholder='Busque um termo ou indicador…'
      aria-label='Buscar um termo ou indicador'
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <Search size={19} aria-hidden='true' />
          </InputAdornment>
        ),
        endAdornment: value ? (
          <InputAdornment position='end'>
            <IconButton
              aria-label='Limpar busca'
              onClick={onClear}
              onMouseDown={(event) => event.preventDefault()}
              edge='end'
              size='small'
            >
              <X size={17} aria-hidden='true' />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      sx={{
        maxWidth: 560,
        '& .MuiOutlinedInput-root': {
          minHeight: 48,
          borderRadius: 1.75,
          backgroundColor: 'hsl(var(--card) / .82)',
          '& fieldset': { borderColor: 'hsl(var(--border))' },
          '&:hover fieldset': { borderColor: 'hsl(var(--primary) / .5)' },
          '&.Mui-focused fieldset': {
            borderColor: 'hsl(var(--primary))',
            borderWidth: 1,
          },
        },
        '& .MuiInputBase-input': { fontSize: { xs: 13, md: 14 } },
      }}
    />
  );
}

function SearchResults({
  results,
  query,
  open,
  resultsId,
  activeIndex,
  onSelect,
}: {
  results: SearchResult[];
  query: string;
  open: boolean;
  resultsId: string;
  activeIndex: number;
  onSelect: (result: SearchResult) => void;
}) {
  if (!open || !query.trim()) return null;

  return (
    <Box
      id={resultsId}
      role='listbox'
      aria-label='Resultados da busca'
      sx={{
        position: 'absolute',
        zIndex: 3,
        top: 'calc(100% + 8px)',
        left: 0,
        width: 'min(560px, 100%)',
        overflow: 'hidden',
        border: '1px solid hsl(var(--border))',
        borderRadius: 1.75,
        backgroundColor: 'hsl(var(--card))',
        boxShadow: '0 18px 42px hsl(var(--foreground) / .14)',
      }}
    >
      {results.length > 0 ? (
        results.map((result, index) => (
          <ButtonBase
            key={result.id}
            id={`${resultsId}-option-${result.id}`}
            component='button'
            role='option'
            aria-selected={index === activeIndex}
            tabIndex={-1}
            onClick={() => onSelect(result)}
            sx={{
              display: 'block',
              width: '100%',
              p: 1.25,
              textAlign: 'left',
              borderBottom: '1px solid hsl(var(--border) / .7)',
              '&:last-child': { borderBottom: 0 },
              backgroundColor: index === activeIndex ? 'hsl(var(--primary) / .1)' : 'transparent',
              '&:hover': { backgroundColor: 'hsl(var(--primary) / .07)' },
              '&:focus-visible': {
                outline: '2px solid hsl(var(--ring))',
                outlineOffset: -2,
              },
            }}
          >
            <Stack direction='row' justifyContent='space-between' gap={1}>
              <Typography fontWeight={850} sx={{ fontSize: 13 }}>
                {result.title}
              </Typography>
              <ArrowUpRight size={14} aria-hidden='true' />
            </Stack>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.25 }}>
              {result.sectionTitle}
            </Typography>
          </ButtonBase>
        ))
      ) : (
        <Typography color='text.secondary' sx={{ p: 1.5, fontSize: 13 }}>
          Nenhum termo encontrado. Tente outra palavra, sigla ou indicador.
        </Typography>
      )}
    </Box>
  );
}

function CategoryCard({ section, onSelect }: { section: ReferenceCategory; onSelect: (sectionId: string) => void }) {
  const accent = getTermAccent(section);
  const Icon = section.icon;

  return (
    <AppLink
      href={`#${section.id}`}
      underline='none'
      color='inherit'
      onClick={(event) => {
        event.preventDefault();
        onSelect(section.id);
      }}
      sx={{
        display: 'flex',
        minHeight: { xs: 118, md: 126 },
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 1,
        p: { xs: 1.5, md: 1.75 },
        border: '1px solid hsl(var(--border))',
        borderRadius: 1.75,
        backgroundColor: 'hsl(var(--card) / .72)',
        transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          borderColor: accent.border,
          boxShadow: '0 12px 24px hsl(var(--foreground) / .08)',
        },
        '&:focus-visible': {
          outline: '2px solid hsl(var(--ring))',
          outlineOffset: 2,
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
      }}
    >
      <Stack direction='row' alignItems='flex-start' gap={1}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: 1,
            color: accent.color,
            backgroundColor: accent.soft,
          }}
        >
          <Icon size={17} strokeWidth={1.9} aria-hidden='true' />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component='h3' sx={{ fontWeight: 850, fontSize: 14 }}>
            {section.title}
          </Typography>
          <Typography color='text.secondary' sx={{ mt: 0.25, fontSize: 12.5, lineHeight: 1.35 }}>
            {section.cardDescription}
          </Typography>
        </Box>
      </Stack>
      <Stack direction='row' flexWrap='wrap' gap={0.5}>
        {section.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size='small'
            sx={{
              height: 23,
              border: '1px solid hsl(var(--border))',
              backgroundColor: 'hsl(var(--muted) / .48)',
              fontSize: 10.5,
              fontWeight: 800,
            }}
          />
        ))}
      </Stack>
    </AppLink>
  );
}

function ReferenceHero({
  query,
  searchResults,
  searchResultsOpen,
  activeSearchIndex,
  onQueryChange,
  onSearchFocus,
  onSearchClose,
  onSearchClear,
  onSearchKeyDown,
  onSearchSelect,
  onSelectSection,
}: {
  query: string;
  searchResults: SearchResult[];
  searchResultsOpen: boolean;
  activeSearchIndex: number;
  onQueryChange: (value: string) => void;
  onSearchFocus: () => void;
  onSearchClose: () => void;
  onSearchClear: () => void;
  onSearchKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onSearchSelect: (result: SearchResult) => void;
  onSelectSection: (sectionId: string) => void;
}) {
  const searchResultsId = useId();
  const activeResultId =
    activeSearchIndex >= 0 && searchResults[activeSearchIndex]
      ? `${searchResultsId}-option-${searchResults[activeSearchIndex].id}`
      : undefined;

  return (
    <Box
      component='section'
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid hsl(var(--border))',
        backgroundColor: 'hsl(var(--page-glow))',
        backgroundImage: 'radial-gradient(circle at 78% 18%, hsl(var(--primary) / .1), transparent 31%)',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1.15fr) minmax(260px, .85fr)',
          },
          gap: { xs: 2, md: 1 },
          alignItems: 'center',
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 3.25, md: 4 },
          pb: { xs: 2.5, md: 3.25 },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <MarketingEyebrow>Conhecimento</MarketingEyebrow>
          <Typography
            component='h1'
            sx={{
              mt: 0.75,
              fontFamily: 'var(--font-display)',
              fontSize: { xs: '2.7rem', sm: '3.6rem', md: '4.25rem' },
              lineHeight: 0.96,
              letterSpacing: '-.055em',
              fontWeight: 800,
              textWrap: 'balance',
            }}
          >
            Referência rápida
          </Typography>
          <Typography
            color='text.secondary'
            sx={{
              mt: 1.25,
              maxWidth: 540,
              lineHeight: 1.55,
              fontSize: { xs: 14, md: 15 },
            }}
          >
            Indicadores, conceitos e termos para consultar enquanto você analisa seus investimentos.
          </Typography>
          <ClickAwayListener onClickAway={onSearchClose}>
            <Box sx={{ position: 'relative', mt: 2.25 }}>
              <SearchField
                value={query}
                onChange={onQueryChange}
                onFocus={onSearchFocus}
                onKeyDown={onSearchKeyDown}
                onClear={onSearchClear}
                resultsId={searchResultsId}
                activeResultId={activeResultId}
                expanded={searchResultsOpen}
              />
              <SearchResults
                query={query}
                results={searchResults}
                open={searchResultsOpen}
                resultsId={searchResultsId}
                activeIndex={activeSearchIndex}
                onSelect={onSearchSelect}
              />
            </Box>
          </ClickAwayListener>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.75, ml: 0.25 }}>
            Ex.: P/VP, WAULT, CRI, duration, IPCA, LTV, marcação a mercado.
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 210,
          }}
        >
          <Image
            src='/referencia-rapida/livros-lupa.webp'
            alt='Livros verdes e uma lupa representando pesquisa e conhecimento'
            width={960}
            height={720}
            priority
            sizes='(max-width: 900px) 34vw, 420px'
            style={{
              width: '100%',
              maxWidth: 420,
              height: 'auto',
              mixBlendMode: 'multiply',
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 1,
          px: { xs: 2, sm: 3, md: 5 },
          pb: { xs: 2.5, md: 3.5 },
        }}
      >
        {referenceCategories.map((section) => (
          <CategoryCard key={section.id} section={section} onSelect={onSelectSection} />
        ))}
      </Box>
    </Box>
  );
}

const visualToneStyles: Record<ReferenceVisualTone, { color: string; background: string; soft: string }> = {
  accent: {
    color: 'hsl(var(--primary))',
    background: 'hsl(var(--primary) / .76)',
    soft: 'hsl(var(--primary) / .42)',
  },
  positive: {
    color: 'hsl(151 35% 36%)',
    background: 'hsl(151 35% 36% / .72)',
    soft: 'hsl(151 35% 36% / .4)',
  },
  warning: {
    color: 'hsl(31 67% 42%)',
    background: 'hsl(31 67% 42% / .72)',
    soft: 'hsl(31 67% 42% / .4)',
  },
  muted: {
    color: 'hsl(var(--muted-foreground))',
    background: 'hsl(var(--muted-foreground) / .42)',
    soft: 'hsl(var(--muted-foreground) / .25)',
  },
};

const getVisualTone = (item: ReferenceVisualItem) => visualToneStyles[item.tone ?? 'accent'];

function VisualHeader({ visual }: { visual: ReferenceVisual }) {
  return (
    <>
      <Stack direction='row' alignItems='center' gap={0.75}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'hsl(var(--primary))',
            boxShadow: '0 0 0 4px hsl(var(--primary) / .1)',
          }}
        />
        <Typography
          variant='overline'
          color='text.secondary'
          sx={{ fontWeight: 900, letterSpacing: 1.05, lineHeight: 1.2 }}
        >
          Exemplo visual
        </Typography>
      </Stack>
      <Typography component='h4' sx={{ mt: 1.25, fontWeight: 850, fontSize: 16, lineHeight: 1.2 }}>
        {visual.title}
      </Typography>
      <Typography color='text.secondary' sx={{ mt: 0.7, fontSize: 12.5, lineHeight: 1.5 }}>
        {visual.description}
      </Typography>
    </>
  );
}

function BarsVisual({ items }: { items: ReferenceVisualItem[] }) {
  const maxRatio = Math.max(...items.map((item) => item.ratio ?? 0), 1);

  return (
    <Stack
      direction='row'
      alignItems='end'
      justifyContent='space-around'
      gap={1}
      sx={{
        height: 154,
        mt: 2.25,
        px: 0.5,
        pt: 2,
        borderBottom: '1px solid hsl(var(--border))',
        backgroundImage: 'linear-gradient(to bottom, hsl(var(--border) / .45) 1px, transparent 1px)',
        backgroundSize: '100% 25%',
      }}
    >
      {items.map((item, index) => {
        const tone = getVisualTone(item);
        const height = `${Math.max(((item.ratio ?? 0) / maxRatio) * 100, 8)}%`;
        return (
          <Stack
            key={`${item.label}-${index}`}
            alignItems='center'
            justifyContent='end'
            gap={0.45}
            sx={{ height: '100%', minWidth: 0, flex: 1 }}
          >
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 9.5,
                fontWeight: 800,
              }}
            >
              {item.value}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 40,
                height,
                borderRadius: '6px 6px 1px 1px',
                background: `linear-gradient(180deg, ${tone.background}, ${tone.soft})`,
              }}
            />
            <Typography
              variant='caption'
              sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 9.5,
                color: tone.color,
              }}
            >
              {item.label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}

function ComparisonVisual({ items }: { items: ReferenceVisualItem[] }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(${Math.min(items.length, 3)}, minmax(0, 1fr))`,
        },
        gap: 1,
        mt: 2.25,
      }}
    >
      {items.map((item, index) => {
        const tone = getVisualTone(item);
        return (
          <Box
            key={`${item.label}-${index}`}
            sx={{
              p: 1.25,
              border: '1px solid hsl(var(--border))',
              borderRadius: 1.25,
              backgroundColor: 'hsl(var(--card) / .68)',
            }}
          >
            <Typography
              variant='overline'
              color='text.secondary'
              sx={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 0.7 }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                mt: 0.65,
                color: tone.color,
                fontSize: 16,
                fontWeight: 900,
              }}
            >
              {item.value}
            </Typography>
            <Box
              sx={{
                height: 7,
                mt: 1.25,
                overflow: 'hidden',
                borderRadius: 99,
                backgroundColor: 'hsl(var(--muted) / .55)',
              }}
            >
              <Box
                sx={{
                  width: `${Math.max(Math.min(item.ratio ?? 0, 100), 4)}%`,
                  height: '100%',
                  borderRadius: 99,
                  backgroundColor: tone.background,
                }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function ScaleVisual({ visual }: { visual: ReferenceVisual }) {
  const marker = Math.max(Math.min(visual.marker ?? 50, 100), 0);

  return (
    <Box sx={{ mt: 2.75, px: 0.5 }}>
      <Box sx={{ position: 'relative', pt: 2.5 }}>
        <Box
          sx={{
            height: 12,
            borderRadius: 99,
            background:
              'linear-gradient(90deg, hsl(151 35% 36% / .52), hsl(var(--primary) / .5), hsl(31 67% 42% / .6))',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: `${marker}%`,
            width: 3,
            height: 38,
            borderRadius: 99,
            backgroundColor: 'hsl(var(--foreground))',
            transform: 'translateX(-50%)',
          }}
        />
        <Typography
          variant='caption'
          sx={{
            position: 'absolute',
            top: -1,
            left: `${marker}%`,
            maxWidth: '45%',
            color: 'text.primary',
            fontSize: 9.5,
            fontWeight: 850,
            textAlign: 'center',
            transform: 'translate(-50%, -100%)',
          }}
        >
          {visual.markerLabel ?? 'referência'}
        </Typography>
      </Box>
      <Stack direction='row' justifyContent='space-between' gap={1} sx={{ mt: 1.25 }}>
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10 }}>
          {visual.leftLabel ?? 'menor'}
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10, textAlign: 'right' }}>
          {visual.rightLabel ?? 'maior'}
        </Typography>
      </Stack>
    </Box>
  );
}

function FlowVisual({ items }: { items: ReferenceVisualItem[] }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems='stretch'
      justifyContent='center'
      gap={0.6}
      sx={{ mt: 2.25 }}
    >
      {items.map((item, index) => {
        const tone = getVisualTone(item);
        return (
          <Stack
            key={`${item.label}-${index}`}
            direction={{ xs: 'row', sm: 'column' }}
            alignItems='center'
            justifyContent='flex-start'
            gap={0.6}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <Box
              sx={{
                width: '100%',
                minHeight: { xs: 50, sm: 76 },
                display: 'grid',
                alignContent: 'center',
                p: 1,
                border: `1px solid ${tone.color}`,
                borderRadius: 1.25,
                textAlign: 'center',
                backgroundColor: 'hsl(var(--card) / .75)',
              }}
            >
              <Typography sx={{ color: tone.color, fontSize: 11, fontWeight: 850 }}>{item.label}</Typography>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 0.35, fontSize: 9.5, lineHeight: 1.25 }}>
                {item.value}
              </Typography>
            </Box>
            {index < items.length - 1 ? (
              <ArrowRight
                size={15}
                color='hsl(var(--muted-foreground))'
                aria-hidden='true'
                className='reference-flow-arrow'
              />
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}

function TimelineVisual({ items }: { items: ReferenceVisualItem[] }) {
  return (
    <Stack
      spacing={1.1}
      sx={{
        position: 'relative',
        mt: 2.25,
        pl: 0.5,
        '&::before': {
          position: 'absolute',
          top: 8,
          bottom: 8,
          left: 7,
          width: '1px',
          content: '""',
          backgroundColor: 'hsl(var(--border))',
        },
      }}
    >
      {items.map((item, index) => {
        const tone = getVisualTone(item);
        return (
          <Stack
            key={`${item.label}-${index}`}
            direction='row'
            alignItems='center'
            gap={1}
            sx={{ position: 'relative' }}
          >
            <Box
              sx={{
                zIndex: 1,
                width: 15,
                height: 15,
                flexShrink: 0,
                border: `3px solid ${tone.color}`,
                borderRadius: '50%',
                backgroundColor: 'hsl(var(--card))',
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: tone.color, fontSize: 11, fontWeight: 850 }}>{item.label}</Typography>
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10 }}>
                {item.value}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}

function StackVisual({ items }: { items: ReferenceVisualItem[] }) {
  const total = items.reduce((sum, item) => sum + Math.max(item.ratio ?? 0, 0), 0) || 1;

  return (
    <Box sx={{ mt: 2.5 }}>
      <Box
        sx={{
          display: 'flex',
          height: 30,
          overflow: 'hidden',
          border: '1px solid hsl(var(--border))',
          borderRadius: 1,
          backgroundColor: 'hsl(var(--muted) / .55)',
        }}
      >
        {items.map((item, index) => {
          const tone = getVisualTone(item);
          return (
            <Box
              key={`${item.label}-${index}`}
              sx={{
                width: `${(Math.max(item.ratio ?? 0, 0) / total) * 100}%`,
                minWidth: 3,
                backgroundColor: tone.background,
                borderRight: index < items.length - 1 ? '1px solid hsl(var(--card) / .75)' : undefined,
              }}
            />
          );
        })}
      </Box>
      <Stack spacing={0.75} sx={{ mt: 1.5 }}>
        {items.map((item, index) => {
          const tone = getVisualTone(item);
          return (
            <Stack key={`${item.label}-${index}`} direction='row' alignItems='center' gap={0.75}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: tone.background,
                }}
              />
              <Typography sx={{ fontSize: 11, fontWeight: 750 }}>{item.label}</Typography>
              <Typography variant='caption' color='text.secondary' sx={{ ml: 'auto', fontSize: 10 }}>
                {item.value}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

function ReadingVisual({ items }: { items: ReferenceVisualItem[] }) {
  return (
    <Stack spacing={0.8} sx={{ mt: 2.25 }}>
      {items.map((item, index) => {
        const tone = getVisualTone(item);
        return (
          <Box
            key={`${item.label}-${index}`}
            sx={{
              p: 1,
              borderLeft: `3px solid ${tone.color}`,
              borderRadius: '0 6px 6px 0',
              backgroundColor: 'hsl(var(--card) / .7)',
            }}
          >
            <Typography sx={{ color: tone.color, fontSize: 10, fontWeight: 900 }}>{item.label}</Typography>
            <Typography color='text.secondary' sx={{ mt: 0.25, fontSize: 11.5, lineHeight: 1.35 }}>
              {item.value}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

function ReferenceVisualPanel({ visual }: { visual: ReferenceVisual }) {
  return (
    <Box
      component='figure'
      data-testid='reference-term-visual'
      sx={{
        m: 0,
        '& .reference-flow-arrow': {
          transform: { xs: 'rotate(90deg)', sm: 'none' },
        },
      }}
    >
      <VisualHeader visual={visual} />
      {visual.kind === 'bars' ? <BarsVisual items={visual.items} /> : null}
      {visual.kind === 'comparison' ? <ComparisonVisual items={visual.items} /> : null}
      {visual.kind === 'scale' ? <ScaleVisual visual={visual} /> : null}
      {visual.kind === 'flow' ? <FlowVisual items={visual.items} /> : null}
      {visual.kind === 'timeline' ? <TimelineVisual items={visual.items} /> : null}
      {visual.kind === 'stack' ? <StackVisual items={visual.items} /> : null}
      {visual.kind === 'reading' ? <ReadingVisual items={visual.items} /> : null}
      <Typography
        component='figcaption'
        variant='caption'
        color='text.secondary'
        sx={{ display: 'block', mt: 1.75, fontSize: 10.5, lineHeight: 1.4 }}
      >
        {visual.note ?? 'Exemplo didático — confirme a fonte e a data antes de comparar.'}
      </Typography>
    </Box>
  );
}

function SectionVisual({ term }: { term: ReferenceTerm }) {
  return (
    <ReferenceVisualPanel
      visual={
        term.visual ?? {
          kind: 'reading',
          title: `Como visualizar ${term.title}`,
          description: term.summary,
          items: [{ label: 'Leitura', value: term.title }],
        }
      }
    />
  );
}

function TermArticle({
  term,
  section,
  featured = false,
  onSelect,
}: {
  term: ReferenceTerm;
  section: ReferenceSection;
  featured?: boolean;
  onSelect: (termId: string) => void;
}) {
  const accent = getTermAccent(section);
  const availability = availabilityLabels[term.availability ?? 'educational'];

  if (!featured) {
    return (
      <Box
        component='article'
        id={term.id}
        sx={{
          scrollMarginTop: { xs: 132, md: 116 },
          p: { xs: 1.5, md: 1.75 },
          border: '1px solid hsl(var(--border))',
          borderRadius: 1.5,
          backgroundColor: 'hsl(var(--card) / .52)',
        }}
      >
        <Typography component='h3' sx={{ fontWeight: 850, fontSize: 15 }}>
          {term.title}
        </Typography>
        <Typography color='text.secondary' sx={{ mt: 0.6, fontSize: 13, lineHeight: 1.5 }}>
          {term.summary}
        </Typography>
        {term.formula ? (
          <Box
            sx={{
              mt: 1.1,
              px: 1,
              py: 0.8,
              borderRadius: 1,
              color: accent.color,
              backgroundColor: accent.soft,
              fontFamily: 'monospace',
              fontSize: 11.5,
            }}
          >
            {term.formula}
          </Box>
        ) : null}
        <AppLink
          href={`#${term.id}`}
          onClick={(event) => {
            event.preventDefault();
            onSelect(term.id);
          }}
          underline='hover'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            mt: 1.25,
            color: accent.color,
            fontSize: 12,
            fontWeight: 850,
          }}
        >
          Ler em destaque <ArrowUpRight size={13} aria-hidden='true' />
        </AppLink>
      </Box>
    );
  }

  return (
    <Box
      component='article'
      id={term.id}
      sx={{
        scrollMarginTop: { xs: 132, md: 116 },
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'minmax(0, 1.08fr) minmax(280px, .92fr)',
        },
        gap: { xs: 2.5, md: 3.5 },
        p: { xs: 1.75, md: 2.5 },
        border: '1px solid hsl(var(--border))',
        borderRadius: 2,
        backgroundColor: 'hsl(var(--card) / .62)',
      }}
    >
      <Box>
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography
            component='h3'
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: { xs: 24, md: 28 },
              lineHeight: 1.05,
              fontWeight: 800,
              letterSpacing: '-.025em',
            }}
          >
            {term.title}
          </Typography>
          <Chip
            label={availability}
            size='small'
            sx={{
              height: 22,
              color: accent.color,
              backgroundColor: accent.soft,
              fontSize: 10,
              fontWeight: 850,
            }}
          />
        </Stack>
        <Typography color='text.secondary' sx={{ mt: 1.1, lineHeight: 1.65, fontSize: 14 }}>
          {term.summary}
        </Typography>
        {term.formula ? (
          <Box
            sx={{
              mt: 1.75,
              p: 1.5,
              border: `1px solid ${accent.border}`,
              borderRadius: 1.25,
              backgroundColor: accent.soft,
            }}
          >
            <Typography sx={{ color: accent.color, fontSize: 12, fontWeight: 850 }}>Fórmula / relação útil</Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontFamily: 'monospace',
                fontSize: { xs: 11.5, md: 12.5 },
                lineHeight: 1.5,
              }}
            >
              {term.formula}
            </Typography>
          </Box>
        ) : null}
        {term.bullets?.length ? (
          <Box sx={{ mt: 1.75 }}>
            <Typography component='h4' sx={{ fontSize: 13, fontWeight: 850 }}>
              O que observar
            </Typography>
            <Stack spacing={0.9} sx={{ mt: 1 }}>
              {term.bullets.map((bullet) => (
                <Stack key={bullet} direction='row' alignItems='flex-start' gap={0.8}>
                  <Check
                    size={15}
                    color={accent.color}
                    strokeWidth={2.2}
                    aria-hidden='true'
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <Typography color='text.secondary' sx={{ fontSize: 13, lineHeight: 1.5 }}>
                    {bullet}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : null}
        {term.warning ? (
          <Stack
            direction='row'
            alignItems='flex-start'
            gap={0.9}
            sx={{
              mt: 1.75,
              p: 1.25,
              borderRadius: 1.25,
              color: 'text.secondary',
              backgroundColor: 'hsl(var(--muted) / .55)',
            }}
          >
            <CircleAlert size={16} color={accent.color} aria-hidden='true' style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12.5, lineHeight: 1.5 }}>{term.warning}</Typography>
          </Stack>
        ) : null}
        {term.cta ? (
          <AppLinkButton
            href={term.cta.href}
            variant='outlined'
            size='small'
            sx={{ mt: 1.75, textTransform: 'none', fontWeight: 850 }}
          >
            {term.cta.label}
            <ArrowUpRight size={14} aria-hidden='true' />
          </AppLinkButton>
        ) : null}
      </Box>
      <Box
        sx={{
          alignSelf: 'stretch',
          p: { xs: 1.25, md: 1.75 },
          border: '1px solid hsl(var(--border))',
          borderRadius: 1.5,
          backgroundColor: 'hsl(var(--background) / .36)',
        }}
      >
        <SectionVisual term={term} />
      </Box>
    </Box>
  );
}

export function ReferenceSectionBlock({
  section,
  selectedTermId,
  onSelectTerm,
}: {
  section: ReferenceSection;
  selectedTermId?: string;
  onSelectTerm: (sectionId: string, termId: string) => void;
}) {
  const selectedTerm = getSectionTerm(section, selectedTermId);
  const accent = getTermAccent(section);

  return (
    <Box component='section' id={section.id} sx={{ scrollMarginTop: { xs: 132, md: 116 } }}>
      <Stack direction='row' alignItems='flex-start' gap={1.25}>
        <SectionIcon section={section} />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component='h2'
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: { xs: 25, md: 31 },
              lineHeight: 1.05,
              letterSpacing: '-.03em',
              fontWeight: 800,
            }}
          >
            {section.title}
          </Typography>
          <Typography color='text.secondary' sx={{ mt: 0.6, maxWidth: 680, fontSize: 13.5, lineHeight: 1.5 }}>
            {section.description}
          </Typography>
        </Box>
      </Stack>
      <Stack direction='row' flexWrap='wrap' gap={0.75} sx={{ mt: 1.75 }}>
        {section.terms.map((term) => {
          const active = term.id === selectedTerm.id;
          return (
            <AppLink
              key={term.id}
              href={`#${term.id}`}
              underline='none'
              onClick={(event) => {
                event.preventDefault();
                onSelectTerm(section.id, term.id);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 31,
                px: 1.15,
                border: `1px solid ${active ? accent.color : 'hsl(var(--border))'}`,
                borderRadius: 1,
                color: active ? 'hsl(var(--primary-foreground))' : 'text.secondary',
                backgroundColor: active ? accent.color : 'hsl(var(--card) / .58)',
                fontSize: 11.5,
                fontWeight: active ? 850 : 700,
                transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
                '&:hover': {
                  borderColor: accent.color,
                  color: active ? 'hsl(var(--primary-foreground))' : accent.color,
                },
                '&:focus-visible': {
                  outline: '2px solid hsl(var(--ring))',
                  outlineOffset: 2,
                },
              }}
            >
              {term.title.split(' — ')[0].split(' (')[0]}
            </AppLink>
          );
        })}
      </Stack>
      <Box sx={{ mt: 1.75 }}>
        <TermArticle
          term={selectedTerm}
          section={section}
          featured
          onSelect={(termId) => onSelectTerm(section.id, termId)}
        />
      </Box>
      {section.terms.length > 1 ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant='overline' color='text.secondary' sx={{ fontWeight: 900, letterSpacing: 1.05 }}>
            Mais conceitos desta seção
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1,
              mt: 0.75,
            }}
          >
            {section.terms
              .filter((term) => term.id !== selectedTerm.id)
              .map((term) => (
                <TermArticle
                  key={term.id}
                  term={term}
                  section={section}
                  onSelect={(termId) => onSelectTerm(section.id, termId)}
                />
              ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

type GlossaryEntry = (typeof glossaryEntries)[number];

function GlossarySection({ onSelectTerm }: { onSelectTerm: (termId: string) => void }) {
  const [query, setQuery] = useState('');
  const [letter, setLetter] = useState('Todos');
  const deferredQuery = useDeferredValue(query);
  const normalizedQuery = normalizeSearchText(deferredQuery);
  const letters = ['Todos', ...Array.from(new Set(glossaryEntries.map(([entryLetter]) => entryLetter)))];
  const entries = useMemo(
    () =>
      glossaryEntries.filter(([entryLetter, title, description]) => {
        const matchesLetter = letter === 'Todos' || entryLetter === letter;
        const matchesQuery =
          !normalizedQuery || normalizeSearchText(`${title} ${description}`).includes(normalizedQuery);
        return matchesLetter && matchesQuery;
      }),
    [letter, normalizedQuery],
  );

  return (
    <Box component='section' id='glossario' sx={{ scrollMarginTop: { xs: 132, md: 116 } }}>
      <Stack direction='row' alignItems='flex-start' gap={1.25}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: { xs: 38, md: 42 },
            height: { xs: 38, md: 42 },
            flexShrink: 0,
            border: '1px solid hsl(var(--primary) / .3)',
            borderRadius: 1.5,
            color: 'primary.main',
            backgroundColor: 'hsl(var(--primary) / .1)',
          }}
        >
          <BookOpen size={21} strokeWidth={1.8} aria-hidden='true' />
        </Box>
        <Box>
          <Typography
            component='h2'
            sx={{
              fontFamily: 'var(--font-display)',
              fontSize: { xs: 25, md: 31 },
              lineHeight: 1.05,
              letterSpacing: '-.03em',
              fontWeight: 800,
            }}
          >
            Glossário A–Z
          </Typography>
          <Typography color='text.secondary' sx={{ mt: 0.6, fontSize: 13.5, lineHeight: 1.5 }}>
            Termos essenciais em ordem alfabética para consultar quando precisar.
          </Typography>
        </Box>
      </Stack>
      <TextField
        fullWidth
        id='glossary-search'
        name='glossary-search'
        autoComplete='off'
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder='Buscar termo no glossário…'
        aria-label='Buscar termo no glossário'
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <Search size={17} aria-hidden='true' />
            </InputAdornment>
          ),
        }}
        sx={{
          mt: 1.75,
          '& .MuiOutlinedInput-root': {
            minHeight: 42,
            borderRadius: 1.25,
            backgroundColor: 'hsl(var(--card) / .6)',
          },
          '& .MuiInputBase-input': { fontSize: 13 },
        }}
      />
      <Typography variant='caption' sx={{ display: 'block', mt: 1.5, fontWeight: 850 }}>
        Navegue pelas letras
      </Typography>
      <Stack direction='row' flexWrap='wrap' gap={0.45} sx={{ mt: 0.75 }}>
        {letters.map((entryLetter) => {
          const active = entryLetter === letter;
          return (
            <ButtonBase
              key={entryLetter}
              component='button'
              onClick={() => setLetter(entryLetter)}
              aria-pressed={active}
              sx={{
                minWidth: entryLetter === 'Todos' ? 54 : 29,
                minHeight: 29,
                px: 0.75,
                border: '1px solid hsl(var(--border))',
                borderRadius: 1,
                color: active ? 'hsl(var(--primary-foreground))' : 'text.secondary',
                backgroundColor: active ? 'hsl(var(--primary))' : 'hsl(var(--muted) / .45)',
                fontSize: 11,
                fontWeight: 850,
                '&:hover': { borderColor: 'hsl(var(--primary) / .5)' },
                '&:focus-visible': {
                  outline: '2px solid hsl(var(--ring))',
                  outlineOffset: 2,
                },
              }}
            >
              {entryLetter}
            </ButtonBase>
          );
        })}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
          mt: 1.75,
        }}
      >
        {entries.map(([entryLetter, title, description, targetId]: GlossaryEntry) => (
          <Box
            key={`${entryLetter}-${title}`}
            component='article'
            id={`glossary-${targetId}`}
            sx={{
              p: { xs: 1.5, md: 1.75 },
              border: '1px solid hsl(var(--border))',
              borderRadius: 1.5,
              backgroundColor: 'hsl(var(--card) / .52)',
            }}
          >
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
              <Typography variant='caption' color='primary' sx={{ fontWeight: 900, letterSpacing: 1 }}>
                {entryLetter}
              </Typography>
              <AppLink
                href={`#${targetId}`}
                onClick={(event) => {
                  event.preventDefault();
                  onSelectTerm(targetId);
                }}
                underline='hover'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.3,
                  color: 'primary.main',
                  fontSize: 11,
                  fontWeight: 850,
                }}
              >
                Explicação completa <ArrowUpRight size={12} aria-hidden='true' />
              </AppLink>
            </Stack>
            <Typography component='h3' sx={{ mt: 0.5, fontWeight: 850, fontSize: 14 }}>
              {title}
            </Typography>
            <Typography color='text.secondary' sx={{ mt: 0.5, fontSize: 12.5, lineHeight: 1.45 }}>
              {description}
            </Typography>
          </Box>
        ))}
      </Box>
      {entries.length === 0 ? (
        <Typography color='text.secondary' sx={{ mt: 2, fontSize: 13 }}>
          Nenhum termo corresponde à busca atual.
        </Typography>
      ) : null}
    </Box>
  );
}

function ReferenceQuickPageContent({ g2rsMode = false }: { g2rsMode?: boolean }) {
  const [query, setQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
  const [selectedTerms, setSelectedTerms] = useState<Record<string, string>>({});
  const deferredQuery = useDeferredValue(query);
  const searchResults = useMemo(() => getSearchResults(deferredQuery), [deferredQuery]);
  const searchResultsOpen = searchFocused && Boolean(query.trim());
  const visibleActiveSearchIndex =
    searchResults.length > 0 ? Math.min(Math.max(activeSearchIndex, -1), searchResults.length - 1) : -1;

  const scrollToId = useCallback((id: string, hash = id) => {
    const target = document.getElementById(id);
    if (!target) return;
    target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' });
    window.history.replaceState(null, '', `#${hash}`);
  }, []);

  const selectTerm = useCallback(
    (sectionId: string, termId: string) => {
      setSelectedTerms((current) => ({ ...current, [sectionId]: termId }));
      setQuery('');
      setSearchFocused(false);
      setActiveSearchIndex(-1);
      window.requestAnimationFrame(() => scrollToId(sectionId, termId));
    },
    [scrollToId],
  );

  const selectSearchResult = useCallback(
    (result: SearchResult) => selectTerm(result.sectionId, result.id),
    [selectTerm],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchFocused(false);
        setActiveSearchIndex(-1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const rawHash = window.location.hash.slice(1);
    if (!rawHash) return;
    let targetId = rawHash;
    try {
      targetId = decodeURIComponent(rawHash);
    } catch {
      // Keep malformed hashes addressable instead of breaking page hydration.
    }
    const match = allReferenceTerms.find((term) => term.id === targetId);
    if (match) {
      setSelectedTerms((current) => ({
        ...current,
        [match.sectionId]: match.id,
      }));
    }
    const frame = window.requestAnimationFrame(() => scrollToId(match?.sectionId ?? targetId, targetId));
    return () => window.cancelAnimationFrame(frame);
  }, [scrollToId]);

  return (
    <MarketingFrame clipOverflow>
      <Box component='main'>
        <ReferenceHero
          query={query}
          searchResults={searchResults}
          searchResultsOpen={searchResultsOpen}
          activeSearchIndex={visibleActiveSearchIndex}
          onQueryChange={(value) => {
            setQuery(value);
            setSearchFocused(true);
            setActiveSearchIndex(value.trim() ? 0 : -1);
          }}
          onSearchFocus={() => {
            setSearchFocused(true);
            if (query.trim() && searchResults.length > 0) {
              setActiveSearchIndex((current) => (current < 0 ? 0 : current));
            }
          }}
          onSearchClose={() => {
            setSearchFocused(false);
            setActiveSearchIndex(-1);
          }}
          onSearchClear={() => {
            setQuery('');
            setSearchFocused(true);
            setActiveSearchIndex(-1);
          }}
          onSearchKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              setSearchFocused(false);
              setActiveSearchIndex(-1);
              return;
            }
            if (event.key === 'ArrowDown' && searchResults.length > 0) {
              event.preventDefault();
              setActiveSearchIndex((current) => (current >= searchResults.length - 1 ? 0 : current + 1));
              return;
            }
            if (event.key === 'ArrowUp' && searchResults.length > 0) {
              event.preventDefault();
              setActiveSearchIndex((current) => (current <= 0 ? searchResults.length - 1 : current - 1));
              return;
            }
            if (event.key === 'Home' && searchResults.length > 0) {
              event.preventDefault();
              setActiveSearchIndex(0);
              return;
            }
            if (event.key === 'End' && searchResults.length > 0) {
              event.preventDefault();
              setActiveSearchIndex(searchResults.length - 1);
              return;
            }
            if (event.key === 'Enter' && searchResults.length > 0) {
              event.preventDefault();
              selectSearchResult(searchResults[visibleActiveSearchIndex] ?? searchResults[0]);
            }
          }}
          onSearchSelect={selectSearchResult}
          onSelectSection={scrollToId}
        />
        <Box
          sx={{
            display: { xs: 'block', md: 'grid' },
            gridTemplateColumns: 'minmax(190px, 225px) minmax(0, 1fr)',
            alignItems: 'start',
            gap: { xs: 2, md: 3 },
            px: { xs: 1.25, sm: 2, md: 3 },
            py: { xs: 2, md: 3 },
          }}
        >
          <ToolSectionNavigation
            items={referenceSectionItems}
            ariaLabel='Seções da referência rápida'
            stickyTop={{ xs: 120, md: 112 }}
          />
          <Box component='article' sx={{ minWidth: 0 }}>
            <Stack spacing={{ xs: 4, md: 5 }}>
              {referenceSections.map((section, index) => (
                <Box key={section.id}>
                  {index > 0 ? <Divider sx={{ mb: { xs: 4, md: 5 } }} /> : null}
                  <ReferenceSectionBlock
                    section={section}
                    selectedTermId={selectedTerms[section.id]}
                    onSelectTerm={selectTerm}
                  />
                </Box>
              ))}
              <Box>
                <Divider sx={{ mb: { xs: 4, md: 5 } }} />
                <GlossarySection
                  onSelectTerm={(termId) => {
                    const term = allReferenceTerms.find((item) => item.id === termId);
                    if (term) selectTerm(term.sectionId, term.id);
                  }}
                />
              </Box>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='space-between'
              alignItems={{ sm: 'center' }}
              gap={1.5}
              sx={{
                mt: { xs: 4, md: 5 },
                pt: 2,
                borderTop: '1px solid hsl(var(--border))',
              }}
            >
              {!g2rsMode ? (
                <Stack direction='row' alignItems='center' gap={1}>
                  <Sparkles size={17} color='hsl(var(--primary))' aria-hidden='true' />
                  <Typography variant='body2' color='text.secondary'>
                    Use o Copiloto para aprofundar um indicador ou comparar fundos.
                  </Typography>
                </Stack>
              ) : null}
              {!g2rsMode ? (
                <AppLink
                  href='/copiloto'
                  underline='hover'
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    color: 'primary.main',
                    fontWeight: 850,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Perguntar ao Copiloto <ArrowUpRight size={14} aria-hidden='true' />
                </AppLink>
              ) : null}
            </Stack>
            <Typography color='text.secondary' sx={{ mt: 3, pb: 1, fontSize: 12, lineHeight: 1.55 }}>
              Conteúdo educacional e de referência. Não constitui recomendação de compra, venda ou manutenção de
              investimentos. Regras tributárias e regulatórias podem mudar; confira as fontes oficiais antes de tomar
              decisões.
            </Typography>
          </Box>
        </Box>
      </Box>
    </MarketingFrame>
  );
}

export default function ReferenceQuickPage({ g2rsMode }: { g2rsMode?: boolean }) {
  return <ReferenceQuickPageContent g2rsMode={g2rsMode} />;
}
